import { PlanType } from '@showbie-socrative/socrative-utils';

export interface Workspace {
  name: string;
  id: string;
  quizzesCount: number;
  mode: WorkspaceMode;
  /**
   * Effective library plan (owner's plan; assessments libraries are Org).
   * Resolved on demand and returned by the **workspace-details** endpoint
   * (`getWorkspace`) — NOT the list — so it's absent on list items. Drives the
   * `isOrgPlan` paywall gate on the library settings surface.
   */
  plan?: PlanType;
  isAssessmentsLibrary?: boolean;
  /**
   * Whether the requesting user has the Showbie Insights add-on. Only
   * meaningful for assessments libraries; false/absent for native Socrative.
   */
  insightsEnabled?: boolean;
}

export enum WorkspaceMode {
  VIEW_ONLY = 0,
  COLLABORATIVE = 1,
}

// Mirrors the backend `WorkspaceSettings` contract from
// services/teacher/refactor/modules/workspaces/types/workspaceSettings.ts.
// Field names are camelCase here because `localAxios` runs `camelizeKeys` on
// every response — the BE's `allow_soc_code` arrives as `allowSocCode` on the
// FE. Outbound writes use `decamelizeKeys` in `patchWorkspaceSettings` so the
// BE's Joi validator still sees the canonical snake_case keys.
export type WorkspaceReadOnlyMode = 'off' | 'admin_only' | 'everyone';

/**
 * Role-floor / threshold value used by member-permission settings.
 *
 * Stored value = the **minimum** workspace role required to perform the
 * gated action. Roles at or above the floor qualify:
 *   - `'viewer'` → Viewers, Editors, Admins, Owners (UI label: "Viewers & above")
 *   - `'editor'` → Editors, Admins, Owners  (UI label: "Editors & above")
 *   - `'admin'`  → Admins, Owners            (UI label: "Admins only")
 *   - `'owner'`  → Owner only                (reserved; no UI yet)
 *
 * This is NOT a member's own role — that's `WorkspaceRole` in
 * `getWorkspaceMembers.ts`. The naming distinction matters: a single user
 * has *one* `WorkspaceRole` on the workspace; settings store a
 * `WorkspaceMemberRoleFloor` to gate actions against it.
 */
export type WorkspaceMemberRoleFloor = 'viewer' | 'editor' | 'admin' | 'owner';

/**
 * Runtime guard for {@link WorkspaceMemberRoleFloor}. Use to validate values
 * arriving from untyped sources (e.g. a shoelace menu's `detail.item.value`)
 * before persisting them, so a future menu change can't silently write an
 * invalid floor.
 */
export function isWorkspaceMemberRoleFloor(
  value: unknown
): value is WorkspaceMemberRoleFloor {
  return (
    value === 'viewer' ||
    value === 'editor' ||
    value === 'admin' ||
    value === 'owner'
  );
}

export interface WorkspaceSettings {
  allowSocCode: boolean;
  hideMemberList: boolean;
  allowMemberInvite: WorkspaceMemberRoleFloor;
  allowDownloadPrint: boolean;
  allowExportAnswerKey: boolean;
  allowCopyToPersonal: boolean;
  allowCopyToLibrary: boolean;
  readOnlyMode: WorkspaceReadOnlyMode;
}

export const DEFAULT_WORKSPACE_SETTINGS: WorkspaceSettings = {
  allowSocCode: false,
  hideMemberList: false,
  allowMemberInvite: 'viewer',
  allowDownloadPrint: true,
  allowExportAnswerKey: true,
  allowCopyToPersonal: true,
  allowCopyToLibrary: true,
  readOnlyMode: 'off',
};
